import { useState } from 'react';
import { Detection, confirmMonth, updateDetectionStatus } from '../api/adminApi';
import { DetectionGroup } from '../pages/DetectionsPage';
import styles from './ConfirmDetectionModal.module.css';

const MONTH_NAMES: Record<number, string> = {
  1: 'Muharram', 2: 'Safar', 3: "Rabi' al-Awwal", 4: "Rabi' al-Thani",
  5: "Jumada al-Awwal", 6: "Jumada al-Thani", 7: 'Rajab', 8: "Sha'ban",
  9: 'Ramadan', 10: 'Shawwal', 11: "Dhu al-Qi'dah", 12: 'Dhu al-Hijjah',
};

const SOURCE_TIER: Record<string, string> = { website: 'Official', social: 'Social', news: 'News' };

interface Props {
  group: DetectionGroup;
  authority: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ConfirmDetectionModal({ group, authority, onClose, onSuccess }: Props) {
  const { lead, detections } = group;
  const { extracted, source, locationCode } = lead;

  const [startDate, setStartDate] = useState(extracted.startDate ?? new Date().toISOString().split('T')[0]);
  const [verified, setVerified] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const monthName = extracted.hijriMonth ? MONTH_NAMES[extracted.hijriMonth] : null;
  const canSubmit = verified && !!startDate && !!extracted.hijriMonth && !!extracted.hijriYear;
  const extraSources = detections.slice(1);

  async function handleConfirm() {
    if (!canSubmit || !extracted.hijriMonth || !extracted.hijriYear) return;
    setSubmitting(true);
    setError(null);
    try {
      await Promise.all([
        confirmMonth({
          locationCode,
          year: extracted.hijriYear,
          month: extracted.hijriMonth,
          startDate,
          source: authority || source.name,
          proofUrls: detections.map((d) => d.source.url).filter(Boolean),
          detectionId: lead.id,
        }),
        ...detections.map((d) => updateDetectionStatus(d.id, 'CONFIRMED')),
      ]);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>
              Confirm {monthName ?? 'month'}{extracted.hijriYear ? ` ${extracted.hijriYear}` : ''}
            </h2>
            <p className={styles.subtitle}>
              {detections.length > 1
                ? `${detections.length} sources agree — review, then confirm.`
                : 'Review the detection, then verify and confirm.'}
            </p>
          </div>
          <button className={styles.close} onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 16 16" fill="currentColor" width="16" height="16">
              <path d="M3.72 3.72a.75.75 0 011.06 0L8 6.94l3.22-3.22a.75.75 0 111.06 1.06L9.06 8l3.22 3.22a.75.75 0 11-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 01-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 010-1.06z" />
            </svg>
          </button>
        </div>

        <div className={styles.body}>
          {/* Lead source summary */}
          <div className={styles.summaryBox}>
            <div className={styles.summaryLabel}>
              <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12"><path d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zM0 8a8 8 0 1116 0A8 8 0 010 8zm6.5-.25A.75.75 0 017.25 7h1a.75.75 0 01.75.75v2.75h.25a.75.75 0 010 1.5h-2a.75.75 0 010-1.5h.25v-2h-.25a.75.75 0 01-.75-.75zM8 6a1 1 0 100-2 1 1 0 000 2z"/></svg>
              {detections.length > 1 ? `Primary source (${detections.length} total)` : 'AI summary'}
            </div>
            <p className={styles.summaryText}>{extracted.reasoning}</p>
            <a href={source.url} target="_blank" rel="noreferrer" className={styles.sourceLink}>
              {source.name} — view source →
            </a>
          </div>

          {/* Corroborating sources */}
          {extraSources.length > 0 && (
            <div className={styles.sourcesBox}>
              <div className={styles.sourcesLabel}>Corroborating sources</div>
              <div className={styles.sourcesList}>
                {extraSources.map((d) => (
                  <SourceRow key={d.id} detection={d} />
                ))}
              </div>
            </div>
          )}

          {/* Start date */}
          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              Official start date of {monthName ?? 'month'}
            </label>
            <input
              type="date"
              className={styles.dateInput}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            {extracted.startDate && (
              <p className={styles.fieldHint}>Pre-filled from detection — verify against the source.</p>
            )}
          </div>

          {/* Verification checkbox */}
          <label className={styles.verifyRow}>
            <input
              type="checkbox"
              checked={verified}
              onChange={(e) => setVerified(e.target.checked)}
              className={styles.checkbox}
            />
            <span className={styles.verifyText}>
              I have verified this against the official announcement from <strong>{authority || source.name}</strong>
            </span>
          </label>

          {!extracted.hijriMonth && (
            <div className={styles.warnBox}>
              The detection did not extract a month number. Cannot confirm without it — dismiss this detection and confirm the month manually.
            </div>
          )}

          {error && <div className={styles.errorBox}>{error}</div>}
        </div>

        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose} disabled={submitting}>Cancel</button>
          <button
            className={styles.confirmBtn}
            onClick={handleConfirm}
            disabled={!canSubmit || submitting}
          >
            {submitting ? 'Confirming…' : `Confirm ${monthName ?? 'month'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function SourceRow({ detection: d }: { detection: Detection }) {
  return (
    <div className={styles.sourceRow}>
      <div className={styles.sourceRowMeta}>
        <span className={styles.sourceRowTier}>{SOURCE_TIER[d.source.type] ?? d.source.type}</span>
        <a href={d.source.url} target="_blank" rel="noreferrer" className={styles.sourceRowName}>
          {d.source.name} →
        </a>
        <span className={styles.sourceRowConf}>{d.extracted.confidence}</span>
      </div>
      <p className={styles.sourceRowReasoning}>{d.extracted.reasoning}</p>
    </div>
  );
}
